import { useCallback, useEffect, useRef, useState } from "react";
import { format, addDays } from "date-fns";
import { Plus, MapPin, Phone, Pencil, Trash2, CalendarPlus, CheckCircle2, XCircle, Paperclip, FileText, Download } from "lucide-react";
import { toast } from "sonner";
import { api, initials, photoUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const EMPTY_PATIENT = { name: "", address: "", phone: "", notes: "" };
const STATUS_STYLES = {
  scheduled: "border-blue-200 bg-blue-50 text-blue-700",
  completed: "border-emerald-200 bg-emerald-50 text-emerald-700",
  cancelled: "border-slate-200 bg-slate-50 text-slate-500",
};

export default function Patients() {
  const [patients, setPatients] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [visits, setVisits] = useState([]);
  const [patientForm, setPatientForm] = useState(EMPTY_PATIENT);
  const [editingPatient, setEditingPatient] = useState(null);
  const [showPatient, setShowPatient] = useState(false);
  const [showVisit, setShowVisit] = useState(false);
  const [visitForm, setVisitForm] = useState({ patient_id: "", employee_id: "", date: new Date().toISOString().slice(0, 10), time: "", purpose: "", status: "scheduled", notes: "" });
  const [confirm, setConfirm] = useState(null); // { title, action }
  const [filesPatient, setFilesPatient] = useState(null);
  const [patientFiles, setPatientFiles] = useState([]);
  const patFileRef = useRef(null);

  const openFiles = useCallback(async (p) => {
    setFilesPatient(p);
    try {
      const r = await api.get(`/patients/${p.id}/attachments`);
      setPatientFiles(r.data);
    } catch {
      toast.error("Failed to load patient files");
    }
  }, []);

  const uploadPatientFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !filesPatient) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/patients/${filesPatient.id}/attachments`, fd);
      toast.success("File uploaded");
      openFiles(filesPatient);
    } catch {
      toast.error("Upload failed");
    } finally {
      if (patFileRef.current) patFileRef.current.value = "";
    }
  };

  const downloadFile = async (att) => {
    try {
      const res = await api.get(`/attachments/${att.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = att.original_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };

  const load = useCallback(async () => {
    try {
      const from = format(new Date(), "yyyy-MM-dd");
      const to = format(addDays(new Date(), 14), "yyyy-MM-dd");
      const [p, e, v] = await Promise.all([
        api.get("/patients"),
        api.get("/employees"),
        api.get(`/visits?from_date=${from}&to_date=${to}`),
      ]);
      setPatients(p.data);
      setEmployees(e.data);
      setVisits(v.data);
    } catch {
      toast.error("Failed to load data");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const savePatient = async () => {
    if (!patientForm.name.trim()) return toast.error("Patient name is required");
    try {
      if (editingPatient) await api.put(`/patients/${editingPatient.id}`, patientForm);
      else await api.post("/patients", patientForm);
      toast.success(editingPatient ? "Patient updated" : "Patient added");
      setShowPatient(false);
      setEditingPatient(null);
      setPatientForm(EMPTY_PATIENT);
      load();
    } catch {
      toast.error("Failed to save patient");
    }
  };

  const deletePatient = async (pid) => {
    await api.delete(`/patients/${pid}`);
    toast.success("Patient removed");
    load();
  };

  const saveVisit = async () => {
    if (!visitForm.patient_id || !visitForm.employee_id) return toast.error("Select a patient and staff member");
    try {
      await api.post("/visits", visitForm);
      toast.success("Visit scheduled");
      setShowVisit(false);
      setVisitForm({ patient_id: "", employee_id: "", date: new Date().toISOString().slice(0, 10), time: "", purpose: "", status: "scheduled", notes: "" });
      load();
    } catch {
      toast.error("Failed to schedule visit");
    }
  };

  const setVisitStatus = async (v, status) => {
    const { patient_name, employee_name, ...payload } = v;
    await api.put(`/visits/${v.id}`, { ...payload, status });
    toast.success(`Visit marked ${status}`);
    load();
  };

  const deleteVisit = async (vid) => {
    await api.delete(`/visits/${vid}`);
    toast.success("Visit removed");
    load();
  };

  return (
    <div className="space-y-5" data-testid="patients-page">
      <div className="flex flex-wrap items-center gap-3">
        <div className="mr-auto">
          <h1 className="font-heading text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">Patients & Visits</h1>
          <p className="mt-1 text-sm text-slate-500">Manage patients and assign staff visits.</p>
        </div>
        <Button variant="outline" className="rounded-md" onClick={() => { setPatientForm(EMPTY_PATIENT); setEditingPatient(null); setShowPatient(true); }} data-testid="add-patient-btn">
          <Plus className="mr-2 h-4 w-4" /> Add Patient
        </Button>
        <Button className="rounded-md bg-[#2C5545] text-white hover:bg-[#1E3B30]" onClick={() => setShowVisit(true)} data-testid="assign-visit-btn">
          <CalendarPlus className="mr-2 h-4 w-4" /> Assign Visit
        </Button>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="space-y-3">
          <h2 className="font-heading text-lg font-medium text-slate-900">Patients ({patients.length})</h2>
          {patients.length === 0 && (
            <Card className="rounded-lg border-dashed border-slate-300" data-testid="patients-empty">
              <CardContent className="py-12 text-center text-sm text-slate-500">No patients yet. Add a patient to start assigning visits.</CardContent>
            </Card>
          )}
          {patients.map((p) => (
            <Card key={p.id} className="rounded-lg border-slate-200 transition-all duration-150 hover:border-slate-300 hover:shadow-sm" data-testid={`patient-card-${p.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-heading text-base font-semibold text-slate-900">{p.name}</p>
                    {p.address && <p className="mt-1 flex items-center gap-1.5 text-xs text-slate-500"><MapPin className="h-3 w-3" /> {p.address}</p>}
                    {p.phone && <p className="mt-0.5 flex items-center gap-1.5 text-xs text-slate-500"><Phone className="h-3 w-3" /> {p.phone}</p>}
                    {p.notes && <p className="mt-1.5 text-xs text-slate-400">{p.notes}</p>}
                  </div>
                  <div className="flex gap-1">
                    <button className="rounded-md p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-900" title="Patient files" onClick={() => openFiles(p)} data-testid={`patient-files-${p.id}`}><Paperclip className="h-3.5 w-3.5" /></button>
                    <button className="rounded-md p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-900" onClick={() => { setPatientForm(p); setEditingPatient(p); setShowPatient(true); }} data-testid={`edit-patient-${p.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                    <button className="rounded-md p-1.5 text-slate-300 transition-colors hover:bg-rose-50 hover:text-rose-600" onClick={() => setConfirm({ title: `Remove ${p.name}? Their visits will also be deleted.`, action: () => deletePatient(p.id) })} data-testid={`delete-patient-${p.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div>
          <h2 className="mb-3 font-heading text-lg font-medium text-slate-900">Upcoming Visits (next 14 days)</h2>
          <Card className="rounded-lg border-slate-200" data-testid="upcoming-visits">
            <CardContent className="max-h-[600px] space-y-2 overflow-y-auto p-4">
              {visits.length === 0 && <p className="py-8 text-center text-sm text-slate-400">No upcoming visits scheduled.</p>}
              {visits.map((v) => (
                <div key={v.id} className="flex items-center gap-3 rounded-md border border-slate-100 p-3" data-testid={`visit-row-${v.id}`}>
                  <Avatar className="h-9 w-9 shrink-0"><AvatarFallback className="bg-slate-100 text-xs font-semibold text-slate-700">{initials(v.employee_name)}</AvatarFallback></Avatar>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-slate-900">{v.patient_name}</p>
                    <p className="text-xs text-slate-500">
                      {format(new Date(v.date + "T12:00:00"), "EEE, MMM d")}{v.time ? ` · ${v.time}` : ""} · {v.employee_name}{v.purpose ? ` · ${v.purpose}` : ""}
                    </p>
                  </div>
                  <Badge variant="outline" className={STATUS_STYLES[v.status]} data-testid={`visit-status-${v.id}`}>{v.status}</Badge>
                  {v.status === "scheduled" && (
                    <>
                      <button className="p-1 text-slate-300 transition-colors hover:text-emerald-600" title="Complete" onClick={() => setVisitStatus(v, "completed")} data-testid={`complete-visit-${v.id}`}><CheckCircle2 className="h-4 w-4" /></button>
                      <button className="p-1 text-slate-300 transition-colors hover:text-slate-500" title="Cancel" onClick={() => setVisitStatus(v, "cancelled")} data-testid={`cancel-visit-${v.id}`}><XCircle className="h-4 w-4" /></button>
                    </>
                  )}
                  <button className="p-1 text-slate-300 transition-colors hover:text-rose-600" onClick={() => setConfirm({ title: "Delete this visit?", action: () => deleteVisit(v.id) })} data-testid={`delete-visit-${v.id}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog open={showPatient} onOpenChange={setShowPatient}>
        <DialogContent className="rounded-lg sm:max-w-md" data-testid="patient-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editingPatient ? "Edit Patient" : "Add Patient"}</DialogTitle><DialogDescription>Patient contact details for visit coordination.</DialogDescription></DialogHeader>
          <div className="space-y-4 pt-2">
            <div><Label htmlFor="pat-name">Name</Label><Input id="pat-name" value={patientForm.name} onChange={(e) => setPatientForm({ ...patientForm, name: e.target.value })} className="mt-1.5" data-testid="patient-name-input" /></div>
            <div><Label htmlFor="pat-address">Address</Label><Input id="pat-address" value={patientForm.address} onChange={(e) => setPatientForm({ ...patientForm, address: e.target.value })} className="mt-1.5" data-testid="patient-address-input" /></div>
            <div><Label htmlFor="pat-phone">Phone</Label><Input id="pat-phone" value={patientForm.phone} onChange={(e) => setPatientForm({ ...patientForm, phone: e.target.value })} className="mt-1.5" data-testid="patient-phone-input" /></div>
            <div><Label htmlFor="pat-notes">Notes</Label><Input id="pat-notes" value={patientForm.notes} onChange={(e) => setPatientForm({ ...patientForm, notes: e.target.value })} className="mt-1.5" data-testid="patient-notes-input" /></div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" className="rounded-md" onClick={() => setShowPatient(false)} data-testid="patient-cancel-btn">Cancel</Button>
              <Button className="rounded-md bg-[#2C5545] text-white hover:bg-[#1E3B30]" onClick={savePatient} data-testid="patient-save-btn">Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showVisit} onOpenChange={setShowVisit}>
        <DialogContent className="rounded-lg sm:max-w-md" data-testid="visit-dialog">
          <DialogHeader><DialogTitle className="font-heading">Assign Patient Visit</DialogTitle><DialogDescription>Link a patient visit to a staff member on a date and time.</DialogDescription></DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label>Patient</Label>
              <Select value={visitForm.patient_id} onValueChange={(v) => setVisitForm({ ...visitForm, patient_id: v })}>
                <SelectTrigger className="mt-1.5" data-testid="visit-patient-select"><SelectValue placeholder="Select patient" /></SelectTrigger>
                <SelectContent>
                  {patients.map((p) => <SelectItem key={p.id} value={p.id} data-testid={`visit-patient-${p.id}`}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Staff Member</Label>
              <Select value={visitForm.employee_id} onValueChange={(v) => setVisitForm({ ...visitForm, employee_id: v })}>
                <SelectTrigger className="mt-1.5" data-testid="visit-employee-select"><SelectValue placeholder="Select staff" /></SelectTrigger>
                <SelectContent>
                  {employees.map((e) => <SelectItem key={e.id} value={e.id} data-testid={`visit-employee-${e.id}`}>{e.name} — {e.position}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label htmlFor="visit-date">Date</Label><Input id="visit-date" type="date" value={visitForm.date} onChange={(e) => setVisitForm({ ...visitForm, date: e.target.value })} className="mt-1.5" data-testid="visit-date-input" /></div>
              <div><Label htmlFor="visit-time">Time</Label><Input id="visit-time" type="time" value={visitForm.time} onChange={(e) => setVisitForm({ ...visitForm, time: e.target.value })} className="mt-1.5" data-testid="visit-time-input" /></div>
            </div>
            <div><Label htmlFor="visit-purpose">Purpose</Label><Input id="visit-purpose" value={visitForm.purpose} onChange={(e) => setVisitForm({ ...visitForm, purpose: e.target.value })} className="mt-1.5" placeholder="e.g. Routine nursing visit, Social work assessment" data-testid="visit-purpose-input" /></div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" className="rounded-md" onClick={() => setShowVisit(false)} data-testid="visit-cancel-btn">Cancel</Button>
              <Button className="rounded-md bg-[#2C5545] text-white hover:bg-[#1E3B30]" onClick={saveVisit} data-testid="visit-save-btn">Schedule Visit</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!filesPatient} onOpenChange={(o) => !o && setFilesPatient(null)}>
        <DialogContent className="rounded-lg sm:max-w-md" data-testid="patient-files-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Files — {filesPatient?.name}</DialogTitle>
            <DialogDescription>Care plans, insurance forms, and other patient documents.</DialogDescription>
          </DialogHeader>
          <div className="pt-2">
            <Button variant="outline" className="mb-3 w-full rounded-full border-[#2C5545]/30 text-[#2C5545] hover:bg-[#2C5545]/10" onClick={() => patFileRef.current?.click()} data-testid="patient-file-upload-btn">
              <Paperclip className="mr-2 h-4 w-4" /> Attach File
            </Button>
            <input ref={patFileRef} type="file" className="hidden" onChange={uploadPatientFile} data-testid="patient-file-input" />
            <div className="max-h-64 space-y-2 overflow-y-auto">
              {patientFiles.length === 0 && <p className="py-6 text-center text-sm text-slate-400">No files attached.</p>}
              {patientFiles.map((att) => (
                <div key={att.id} className="flex items-center gap-2.5 rounded-md border border-slate-100 p-2.5" data-testid={`patient-file-${att.id}`}>
                  <FileText className="h-4 w-4 shrink-0 text-slate-400" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-xs font-medium text-slate-700">{att.original_filename}</p>
                    <p className="text-[10px] text-slate-400">{att.size < 1024 ? `${att.size} B` : `${Math.round(att.size / 1024)} KB`}</p>
                  </div>
                  <button className="p-1 text-slate-400 transition-colors hover:text-slate-900" onClick={() => downloadFile(att)} data-testid={`patient-file-download-${att.id}`}><Download className="h-3.5 w-3.5" /></button>
                  <button className="p-1 text-slate-300 transition-colors hover:text-rose-600" onClick={() => setConfirm({ title: `Remove ${att.original_filename}?`, action: async () => { try { await api.delete(`/attachments/${att.id}`); openFiles(filesPatient); } catch { toast.error("Failed to remove file"); } } })} data-testid={`patient-file-remove-${att.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent className="rounded-lg" data-testid="confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm?.title}</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-rose-600 hover:bg-rose-700" onClick={() => { confirm?.action(); setConfirm(null); }} data-testid="confirm-action-btn">Confirm</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
